import ReactApexChart from "react-apexcharts";
import { Box } from "@mui/material";
import {colors} from "~src/data/brandTabColors.ts"
import { MetricArrayData } from "../TrendByCompetitors";
import { BrandResult } from "../SearchBar";
import { formatMonthYear } from "~src/utils/formatChart";
import usePermissions from "~src/hooks/usePermissions";

export default function TrendByCompetitorGraph({
  metricsChartData,
  selectedBrands,
}: {
  metricsChartData: MetricArrayData[];
  selectedBrands: BrandResult[] | undefined;
}) {
  const { premiumCommunity } = usePermissions();
  return (
    <Box>
      <ApexChart
        graphData={metricsChartData}
        selectedBrands={selectedBrands}
        premiumCommunity={premiumCommunity}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mt: 2,
        }}
      ></Box>
    </Box>
  );
}

interface ApexChartProps {
  graphData: MetricArrayData[];
  selectedBrands: BrandResult[] | undefined;
  premiumCommunity: boolean;
}

const ApexChart: React.FC<ApexChartProps> = ({
  graphData,
  selectedBrands,
  premiumCommunity,
}) => {
  const longestDateIndex = graphData.reduce((acc, data, index) => {
    if (data.date.length > graphData[acc].date.length) {
      return index;
    }
    return acc;
  }, 0);

  const longestDates = graphData[longestDateIndex].date;

  const seriesData = graphData.map((data, index) => {
    if (
      !premiumCommunity &&
      (index === graphData.length - 2 || index === graphData.length - 1)
    ) {
      return {
        name:
          (selectedBrands && selectedBrands[index]?.brand_name) ||
          `Brand ${index + 1}`,
        data: Array(data.data.length).fill(null),
      };
    }

    return {
      name:
        (selectedBrands && selectedBrands[index]?.brand_name) ||
        `Brand ${index + 1}`,
      data: data.data,
    };
  });

  function balanceDataArrays(
    seriesData: {
      name: string;
      data: any[];
    }[],
  ) {
    // Find the maximum length of the data arrays
    const maxLength = seriesData.reduce(
      (max: any, item: any) => Math.max(max, item.data.length),
      0,
    );

    // Pad each data array to the maximum length with zeros
    return seriesData.map((item) => {
      const paddedData = [...item.data];
      while (paddedData.length < maxLength) {
        paddedData.push(0);
      }
      return {
        ...item,
        data: paddedData,
      };
    });
  }

  const config: ApexCharts.ApexOptions = {
    series: balanceDataArrays(seriesData),
    chart: {
      type: "area",
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    colors: colors,
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 0.9,
        opacityFrom: 0.75,
        opacityTo: 0,
      },
    },
    xaxis: {
      categories: longestDates.map((value) => formatMonthYear(value)),
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      labels: {
        show: true,
        formatter: (value: number) => value === null || value === undefined ? '0' : value.toLocaleString(),
      },
    },
    legend: {
      show: true,
      formatter: (seriesName: string, opts: any) => {
        const index = opts.seriesIndex;
        if (
          !premiumCommunity &&
          ((seriesData.length === 5 &&
            (index === seriesData.length - 2 ||
              index === seriesData.length - 1)) ||
            (seriesData.length === 4 && index === seriesData.length - 1))
        ) {
          return `<span style="position:relative;top:2px;"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 17C12.5523 17 13 16.5523 13 16C13 15.4477 12.5523 15 12 15C11.4477 15 11 15.4477 11 16C11 16.5523 11.4477 17 12 17ZM18 9V7C18 4.23858 15.7614 2 13 2C10.2386 2 8 4.23858 8 7V9H6V19H18V9H16ZM10 7C10 5.34315 11.3431 4 13 4C14.6569 4 16 5.34315 16 7V9H10V7Z" stroke="currentColor" fill="none"/>
        </svg></span>`;
        }
        return seriesName;
      },
    },
    grid: {
      show: true,
    },
  };

  return (
    <ReactApexChart
      options={config}
      series={config.series}
      type="area"
      height={350}
    />
  );
};
